import {
  Download,
  ExternalLink,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { MdClose, MdInsertDriveFile, MdPictureAsPdf, MdSearch, MdTableChart } from 'react-icons/md';
import ExcelPreview from '../components/ExcelPreview';
import { useSetPageHeader } from '../contexts/PageHeaderContext';
import { DownloadedFile, getDownloadFiles } from '../utils/localStore.util';
import { usePageMeta } from '../utils/pageMeta.util';

const isPdf = (fileType?: string) => fileType?.toLowerCase() === 'pdf';
const isExcel = (fileType?: string) =>
  ['excel', 'xlsx', 'xls', 'csv'].includes(fileType?.toLowerCase() ?? '');

const FileIcon = ({ fileType, size = 20 }: { fileType?: string; size?: number }) => {
  if (isPdf(fileType)) return <MdPictureAsPdf size={size} className="text-red-500" />;
  if (isExcel(fileType)) return <MdTableChart size={size} className="text-green-600" />;
  return <MdInsertDriveFile size={size} className="text-slate-400" />;
};

const FileTypeBadge = ({ fileType }: { fileType?: string }) => {
  if (isPdf(fileType))
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-500 border border-red-100">
        PDF
      </span>
    );
  if (isExcel(fileType))
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-600 border border-green-100">
        Excel
      </span>
    );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-50 text-slate-400 border border-slate-100">
      File
    </span>
  );
};

const DownloadsPage: React.FC = () => {
  usePageMeta({
    title: 'Downloads | HCAE Client Portal',
    description: 'Review files you previously downloaded from Documents and open them quickly from the HCAE Client Portal.',
  });
  useSetPageHeader('Downloads', "Files you've previously downloaded");

  const [downloads, setDownloads] = useState<DownloadedFile[]>([]);
  const [preview, setPreview] = useState<DownloadedFile | null>(null);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setDownloads(getDownloadFiles());
  }, []);

  useEffect(() => {
    setPreviewLoaded(false);
  }, [preview?.id]);

  const filteredDownloads = downloads.filter((f) =>
    f.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-GB').replace(/\//g, '-');

  const renderPreviewContent = (file: DownloadedFile) => {
    if (isPdf(file.file_type)) {
      return (
        <div className="relative bg-slate-50 flex-1 overflow-hidden">
          {!previewLoaded && (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <div className="w-full h-full p-4 sm:p-6">
                <div className="h-full w-full rounded-xl bg-white border border-slate-100 shadow-sm animate-pulse" />
              </div>
            </div>
          )}
          <iframe
            src={`${file.url}#toolbar=0&navpanes=0&scrollbar=0`}
            className="w-full"
            style={{ height: 'min(70vh, calc(100vh - 360px))' }}
            title={file.title}
            onLoad={() => setPreviewLoaded(true)}
          />
        </div>
      );
    }

    if (isExcel(file.file_type)) {
      return (
        <ExcelPreview
          url={file.url}
          title={file.title}
          height="min(70vh, calc(100vh - 360px))"
        />
      );
    }

    return (
      <div className="relative bg-slate-50" style={{ height: 'min(70vh, calc(100vh - 360px))' }}>
        {!previewLoaded && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="w-full h-full p-4 sm:p-6">
              <div className="h-full w-full rounded-xl bg-white border border-slate-100 shadow-sm animate-pulse" />
            </div>
          </div>
        )}
        <iframe
          src={`${file.url}#toolbar=0&navpanes=0&scrollbar=0`}
          className="w-full h-full"
          title={file.title}
          onLoad={() => setPreviewLoaded(true)}
        />
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-5">
      {/* Search bar */}
      <div className="relative w-full sm:max-w-xs">
        <MdSearch size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPreview(null); }}
          placeholder="Search downloads…"
          className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-slate-200 rounded-2xl shadow-sm placeholder-slate-400 text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-300 transition"
        />
        {searchQuery && (
          <button
            onClick={() => { setSearchQuery(''); setPreview(null); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
          >
            <MdClose size={15} />
          </button>
        )}
      </div>

      {preview ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
          <div className="space-y-3">
            {filteredDownloads.map((file) => (
              <button
                key={file.id}
                onClick={() => setPreview(file)}
                className={`w-full text-left flex items-center gap-3 p-4 rounded-2xl border transition-all duration-200 ${preview?.id === file.id
                  ? 'border-brand-200 bg-brand-50 shadow-md'
                  : 'border-brand-100 bg-white hover:border-brand-200 hover:shadow-sm'
                  }`}
              >
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0">
                  <FileIcon fileType={file.file_type} size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-800 truncate">{file.title}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <FileTypeBadge fileType={file.file_type} />
                    <p className="text-xs text-slate-400">{formatDate(file.downloadedAt)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-5 py-4 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <FileIcon fileType={preview.file_type} size={18} />
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-800 text-sm truncate">{preview.title}</h3>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <FileTypeBadge fileType={preview.file_type} />
                    <p className="text-xs text-slate-400">{formatDate(preview.downloadedAt)}</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 sm:justify-end shrink-0">
                <button
                  onClick={() => window.open(preview.url, '_blank', 'noopener,noreferrer')}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-white text-slate-600 text-xs font-semibold rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors flex-1 sm:flex-none"
                >
                  <ExternalLink size={14} />
                  Open
                </button>
                <button
                  onClick={() => setPreview(null)}
                  className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors shrink-0"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {renderPreviewContent(preview)}
          </div>
        </div>
      ) : downloads.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 flex flex-col items-center justify-center py-24">
          <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-300 mb-4">
            <Download size={28} />
          </div>
          <p className="text-slate-500 font-semibold">No downloads yet</p>
          <p className="text-slate-400 text-sm mt-1">
            Files you download from Documents will appear here
          </p>
        </div>
      ) : filteredDownloads.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 flex flex-col items-center justify-center py-24">
          <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-300 mb-4">
            <MdSearch size={28} className="text-brand-300" />
          </div>
          <p className="text-slate-500 font-semibold">No results found</p>
          <p className="text-slate-400 text-sm mt-1">Try a different search term</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-4">
          {filteredDownloads.map((file) => (
            <button
              key={file.id}
              onClick={() => setPreview(file)}
              className="w-full text-left bg-white rounded-2xl border border-brand-100 p-4 flex items-center gap-4 hover:shadow-md hover:border-brand-200 transition-all duration-200 group"
            >
              <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center shrink-0 group-hover:bg-slate-100 transition-colors">
                <FileIcon fileType={file.file_type} size={22} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-800 truncate">{file.title}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <FileTypeBadge fileType={file.file_type} />
                  <p className="text-xs text-slate-400">{formatDate(file.downloadedAt)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DownloadsPage;
